import { apis, privateApis } from "../../../backend";
import { CallDisconnectReason } from "../../../models/CallDisconnectReason";
import { getTabId } from "../../../utils/tabs";
import { getAccessToken } from "../../../utils/user";
import { CallMetadata } from "../TelephonyService";

export type VertoLogin = {
  username: string;
  password: string;
  domain: string;
  url: string;
}

export async function createVertoLoginAsDetector() {
  return await privateApis.dial.post<VertoLogin>('/call-detectors-v1/login');
}

/**
 * Creates a login ("temporary phone line") for logging in using softphone.
 */
export async function createVertoLogin() {
  return await apis.dial.post<VertoLogin>('/easytelecom-v1/login');
}

export async function createEasyTelecomOutboundCall(easyTelecomUserCallId: string, callerPhoneNumber: string, destinationPhoneNumber: string, metadata?: CallMetadata, debug?: string[]) {
  return await apis.dial.post<{ callId: string }>('/easytelecom-v1/call', {
    ...metadata,
    easyTelecomUserCallId,
    callerPhoneNumber,
    destinationPhoneNumber,
    dialGroupId: metadata.leadListId,
    dialGroupMode: metadata.leadListMode,
    shuffleDialGroupId: metadata.shuffleLeadListId,
    debug,
  });
}

/**
 * Forces hang-up of both parties in call.
 */
export async function hangUp(easyTelecomUserCallId: string, reason: CallDisconnectReason | undefined | null) {
  await apis.dial.put(`/easytelecom-v1/hangup/${easyTelecomUserCallId}`, { reason });
}

export enum PredictiveModeUserUpdateType {
  Background = 'background'
}

export type PredictiveModeUserUpdate = {
  easyTelecomUserCallId: string;
  projectId?: string;
  
  /**
   * Determines whether to apply the update.
   */
  type?: PredictiveModeUserUpdateType;
}

/**
 * Sets the referenced user call, required to be routed predictive calls.
 */
export async function updatePredictiveModeUser(leadListId: string, update: PredictiveModeUserUpdate) {
  return await apis.dial.put(`/easytelecom-predictive-dial-v1/parked-call/${leadListId}`, update);
}

/**
 * Sets the referenced user call, required to be routed predictive calls.
 */
export async function updateUserCohearingParkedCall(easyTelecomUserCallId: string) {
  return await apis.dial.put(`/cohearing-v1/parked-call`, {
    easyTelecomUserCallId,
  }, { tabId: getTabId() });
}

/**
 * Begins cohearing call with ID `callId`.
 */
export async function cohearCall(callId: string, easyTelecomUserCallId: string) {
  await apis.dial.post(`/easytelecom-v1/cohear/${callId}`, { easyTelecomUserCallId });
}

/**
 * Creates a user cohear session.
 */
export async function cohearUser(userId: string, easyTelecomUserCallId: string) {
  await apis.dial.post(`/cohearing-v1/user/${userId}`, {
    easyTelecomUserCallId,
    whisper: true,
  }, { tabId: getTabId() });
}

/**
 * Ends the current user cohearing session, if any.
 */
export async function endUserCohearingSession() {
  await apis.dial.del('/cohearing-v1/user/end', { tabId: getTabId() });
}

/**
 * Answers an incoming EasyTelecom call.
 */
export async function answerInboundEasyTelecomCall(callId: string, easyTelecomUserCallId: string) {
  await apis.dial.post(`/easytelecom-inbound-calls-v1/answer/${callId}`, { easyTelecomUserCallId });
}

/**
 * Sends an acknowledgement that a co-hearing call is connected.
 */
export function acknowledgeCohearing(easyTelecomUserCallId?: string) {
  const opts = new Blob([JSON.stringify({
    authorization: getAccessToken()
  })], { type: 'application/json' });

  const url = new URL(apis.dial.getUrl('/cohearing-v1/acknowledge'), document.location.origin);

  if (easyTelecomUserCallId) {
    url.searchParams.set('easyTelecomUserCallId', easyTelecomUserCallId);
  }

  if (!navigator.sendBeacon(url.href, opts)) {
    throw new Error("sendBeacon returned false");
  }
}